<template>
  <Teleport to="body">
    <Modal v-model="show">
      <ModalContent size="large">
        <template #header>
          <Heading level="h2">{{ t('settings_modal.title') }}</Heading>
        </template>

        <div class="tw-space-y-5">
          <section>
            <Heading level="h4">{{ t('settings_modal.preview.title') }}</Heading>
            <div class="tw-mt-3 tw-grid tw-gap-5 md:tw-grid-cols-2">
              <FormField
                v-model="previewQualityLocal"
                type="select"
                :label="t('settings_modal.preview.preview_quality.label')"
              >
                <option value="low">
                  {{ t('settings_modal.preview.preview_quality.options.low') }}
                </option>
                <option value="medium">
                  {{ t('settings_modal.preview.preview_quality.options.medium') }}
                </option>
                <option value="high">
                  {{ t('settings_modal.preview.preview_quality.options.high') }}
                </option>
              </FormField>

              <FormField
                v-model.number="previewFpsLocal"
                type="number"
                :label="t('settings_modal.preview.fps.label')"
                :min="1"
              />

              <FormField
                v-model.number="previewEncoderQualityLocal"
                type="number"
                :label="t('settings_modal.preview.encoder_quality.label')"
                :min="1"
              />

              <FormField
                v-model="previewEncoderTypeLocal"
                type="select"
                :label="t('settings_modal.preview.encoder_type.label')"
              >
                <option value="webp">webp</option>
                <option value="jpg">jpg</option>
              </FormField>

              <FormField
                :value="previewBitrateLocal"
                readonly
                :label="t('settings_modal.preview.approximate_bitrate.label')"
              />
            </div>
          </section>

          <section>
            <Heading level="h4">{{ t('settings_modal.monitor.title') }}</Heading>

            <div class="tw-mt-3 tw-grid tw-gap-5 md:tw-grid-cols-2">
              <FormField
                v-model="monitorQualityLocal"
                type="select"
                :label="t('settings_modal.monitor.preview_quality.label')"
              >
                <option value="low">
                  {{ t('settings_modal.monitor.preview_quality.options.low') }}
                </option>
                <option value="medium">
                  {{ t('settings_modal.monitor.preview_quality.options.medium') }}
                </option>
                <option value="high">
                  {{ t('settings_modal.monitor.preview_quality.options.high') }}
                </option>
              </FormField>

              <FormField
                v-model.number="monitorFpsLocal"
                type="number"
                :label="t('settings_modal.monitor.fps.label')"
                :min="1"
              />

              <FormField
                v-model.number="monitorEncoderQualityLocal"
                type="number"
                :label="t('settings_modal.monitor.encoder_quality.label')"
                :min="1"
              />

              <FormField
                v-model="monitorEncoderTypeLocal"
                type="select"
                :label="t('settings_modal.monitor.encoder_type.label')"
              >
                <option value="webp">webp</option>
                <option value="jpg">jpg</option>
              </FormField>

              <FormField
                :value="monitorBitrateLocal"
                readonly
                :label="t('settings_modal.monitor.approximate_bitrate.label')"
              />
            </div>
          </section>

          <section>
            <Heading level="h4">{{ t('settings_modal.audio.title') }}</Heading>

            <div class="tw-mt-3 tw-grid tw-gap-5 md:tw-grid-cols-2">
              <FormField
                v-model="audioMonitorMixLocal"
                type="select"
                :label="t('settings_modal.audio.monitor_mix.label')"
              >
                <option v-for="{ value, label } in audioMonitorMixOptions" :key="value" :value="value">
                  {{ label }}
                </option>
              </FormField>
            </div>
          </section>

          <section>
            <Heading level="h4">{{ t('settings_modal.connection.title') }}</Heading>
            <div class="tw-mt-3 tw-grid tw-gap-5 md:tw-grid-cols-2">
              <FormField v-model="connectionModeLocal" type="select" :label="t('settings_modal.connection.mode.label')">
                <option value="auto">
                  {{ t('settings_modal.connection.mode.options.auto') }}
                  {{
                    connectionModeLocal === 'auto'
                      ? `(→ ${t(`settings_modal.connection.mode.options.${resolvedMode}`)})`
                      : ''
                  }}
                </option>
                <option value="direct">{{ t('settings_modal.connection.mode.options.direct') }}</option>
                <option value="proxy">{{ t('settings_modal.connection.mode.options.proxy') }}</option>
              </FormField>

              <FormField
                v-model="regionPreferenceLocal"
                type="select"
                :label="t('settings_modal.connection.region.label')"
                :disabled="resolvedMode !== 'proxy'"
              >
                <option value="auto">{{ t('settings_modal.connection.region.options.auto') }}</option>
                <option value="EU">EU</option>
                <option value="US">US</option>
              </FormField>
            </div>

            <div v-if="resolvedMode === 'proxy'" class="tw-mt-3 tw-flex tw-items-center tw-gap-3">
              <span class="tw-text-sm tw-text-white/68">
                {{ t('settings_modal.connection.latency.label') }}: {{ latencyLabel }}
              </span>
              <Button color="transparent" outlined :disabled="retesting" @click="retestLatency">
                {{ t('settings_modal.connection.retest') }}
              </Button>
            </div>

            <div class="tw-mt-3">
              <Checkbox
                v-model="warnInternalLinksLocal"
                :right-label="t('settings_modal.connection.warn_internal_links')"
              />
            </div>
          </section>
        </div>

        <template #footer>
          <div class="tw-flex tw-w-full tw-justify-end tw-gap-2">
            <Button color="transparent" outlined @click="cancel">
              {{ t('common.cancel') }}
            </Button>
            <Button color="primary" @click="save">
              {{ t('common.save') }}
            </Button>
          </div>
        </template>
      </ModalContent>
    </Modal>
  </Teleport>
</template>

<script setup>
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { Button, Checkbox, FormField, Heading, Modal, ModalContent } from '@slideslive/fuse-kit/vue';

import estimatePreviewBitrate from '@/helpers/estimate_preview_bitrate';
import useSettingsStore from '@/stores/settings';
import useStationsStore from '@/stores/stations';
import useVpnStore from '@/stores/vpn';

const show = defineModel();
const { t } = useI18n();

const settingsStore = useSettingsStore();
const {
  connectionMode,
  regionPreference,
  warnBeforeInternalLinks,
  previewQuality,
  previewFps,
  previewEncoderQuality,
  previewEncoderType,
  monitorQuality,
  monitorFps,
  monitorEncoderQuality,
  monitorEncoderType,
  audioMonitorMix,
} = storeToRefs(settingsStore);

const vpnStore = useVpnStore();
const { mode: resolvedMode, latencies } = storeToRefs(vpnStore);
const stationsStore = useStationsStore();

const connectionModeLocal = ref('auto');
const regionPreferenceLocal = ref('auto');
const warnInternalLinksLocal = ref(true);
const retesting = ref(false);

const latencyLabel = computed(() => {
  const format = (value) => {
    if (value == null) return '—';
    return Number.isFinite(value) ? `${Math.round(value)} ms` : t('settings_modal.connection.latency.unreachable');
  };

  return `EU ${format(latencies.value.EU)} · US ${format(latencies.value.US)}`;
});

async function retestLatency() {
  retesting.value = true;
  try {
    await vpnStore.refresh({ remeasure: true });
    await stationsStore.refreshAll({ silent: true });
  } finally {
    retesting.value = false;
  }
}

const previewQualityLocal = ref('low');
const previewFpsLocal = ref(2);
const previewEncoderQualityLocal = ref(5);
const previewEncoderTypeLocal = ref('webp');

const monitorQualityLocal = ref('medium');
const monitorFpsLocal = ref(12);
const monitorEncoderQualityLocal = ref(30);
const monitorEncoderTypeLocal = ref('webp');

const audioMonitorMixOptions = [
  { value: 'LR', label: 'L+R' },
  { value: 'LL', label: 'L+L' },
  { value: 'RR', label: 'R+R' },
  { value: 'mono', label: 'mono' },
];

const audioMonitorMixLocal = ref('LR');

const PREVIEW_PRESETS = {
  low: { fps: 2, encoderQuality: 5, encoderType: 'webp' },
  medium: { fps: 2, encoderQuality: 10, encoderType: 'webp' },
  high: { fps: 12, encoderQuality: 20, encoderType: 'webp' },
};

const MONITOR_PRESETS = {
  low: { fps: 5, encoderQuality: 10, encoderType: 'webp' },
  medium: { fps: 12, encoderQuality: 30, encoderType: 'webp' },
  high: { fps: 12, encoderQuality: 60, encoderType: 'webp' },
};

watch(
  () => show.value,
  (visible) => {
    if (!visible) return;

    connectionModeLocal.value = connectionMode.value;
    regionPreferenceLocal.value = regionPreference.value;
    warnInternalLinksLocal.value = warnBeforeInternalLinks.value;

    previewQualityLocal.value = previewQuality.value;
    previewFpsLocal.value = previewFps.value;
    previewEncoderQualityLocal.value = previewEncoderQuality.value;
    previewEncoderTypeLocal.value = previewEncoderType.value;

    monitorQualityLocal.value = monitorQuality.value;
    monitorFpsLocal.value = monitorFps.value;
    monitorEncoderQualityLocal.value = monitorEncoderQuality.value;
    monitorEncoderTypeLocal.value = monitorEncoderType.value;

    audioMonitorMixLocal.value = audioMonitorMix.value;
  },
);

watch(
  () => previewQualityLocal.value,
  (quality) => {
    const preset = PREVIEW_PRESETS[quality];
    if (!preset) return;

    previewFpsLocal.value = preset.fps;
    previewEncoderQualityLocal.value = preset.encoderQuality;
    previewEncoderTypeLocal.value = preset.encoderType;
  },
);

watch(
  () => monitorQualityLocal.value,
  (quality) => {
    const preset = MONITOR_PRESETS[quality];
    if (!preset) return;

    monitorFpsLocal.value = preset.fps;
    monitorEncoderQualityLocal.value = preset.encoderQuality;
    monitorEncoderTypeLocal.value = preset.encoderType;
  },
);

const previewBitrateLocal = computed(() =>
  estimatePreviewBitrate({
    encoderType: previewEncoderTypeLocal,
    encoderQuality: previewEncoderQualityLocal,
    fps: previewFpsLocal,
  }),
);

const monitorBitrateLocal = computed(() =>
  estimatePreviewBitrate({
    encoderType: monitorEncoderTypeLocal,
    encoderQuality: monitorEncoderQualityLocal,
    fps: monitorFpsLocal,
  }),
);

function cancel() {
  show.value = false;
}

function save() {
  connectionMode.value = connectionModeLocal.value;
  regionPreference.value = regionPreferenceLocal.value;
  if (warnInternalLinksLocal.value) {
    settingsStore.clearInternalLinkWarningSuppression();
  } else {
    settingsStore.suppressInternalLinkWarning();
  }
  previewQuality.value = previewQualityLocal.value;
  previewFps.value = previewFpsLocal.value;
  previewEncoderQuality.value = previewEncoderQualityLocal.value;
  previewEncoderType.value = previewEncoderTypeLocal.value;
  monitorQuality.value = monitorQualityLocal.value;
  monitorFps.value = monitorFpsLocal.value;
  monitorEncoderQuality.value = monitorEncoderQualityLocal.value;
  monitorEncoderType.value = monitorEncoderTypeLocal.value;
  audioMonitorMix.value = audioMonitorMixLocal.value;
  show.value = false;
}
</script>
